/**
 * Zustand store for sharing preferences and recently used items
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { log } from '../utils/errors/logger';

interface SharingState {
  recentlyUsedGroups: string[];
  recentlyUsedUsers: string[];
  preferredVisibility: string;
  maxRecentItems: number;
}

interface SharingActions {
  addRecentGroup: (groupId: string) => void;
  addRecentUser: (userId: string) => void;
  removeRecentGroup: (groupId: string) => void;
  removeRecentUser: (userId: string) => void;
  setPreferredVisibility: (visibility: string) => void;
  clearRecentGroups: () => void;
  clearRecentUsers: () => void;
  reset: () => void;
}

type SharingStore = SharingState & SharingActions;

const initialState: SharingState = {
  recentlyUsedGroups: [],
  recentlyUsedUsers: [],
  preferredVisibility: 'private',
  maxRecentItems: 5
};

export const useSharingStore = create<SharingStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      addRecentGroup: (groupId: string) => {
        const { recentlyUsedGroups, maxRecentItems } = get();
        
        // Remove if already exists (move to front)
        const filtered = recentlyUsedGroups.filter(id => id !== groupId);
        
        // Add to front and limit to maxRecentItems
        const updated = [groupId, ...filtered].slice(0, maxRecentItems);
        
        set({ recentlyUsedGroups: updated });
        
        log.user.action('recent_group_added', 'SharingStore', { 
          groupId,
          totalRecent: updated.length 
        });
      },

      addRecentUser: (userId: string) => {
        const { recentlyUsedUsers, maxRecentItems } = get();
        
        // Remove if already exists (move to front)
        const filtered = recentlyUsedUsers.filter(id => id !== userId);
        
        // Add to front and limit to maxRecentItems
        const updated = [userId, ...filtered].slice(0, maxRecentItems);
        
        set({ recentlyUsedUsers: updated });
        
        log.user.action('recent_user_added', 'SharingStore', { 
          userId,
          totalRecent: updated.length 
        });
      },

      removeRecentGroup: (groupId: string) => {
        const { recentlyUsedGroups } = get();
        const updated = recentlyUsedGroups.filter(id => id !== groupId);
        
        set({ recentlyUsedGroups: updated });
        
        log.user.action('recent_group_removed', 'SharingStore', { groupId });
      },

      removeRecentUser: (userId: string) => {
        const { recentlyUsedUsers } = get();
        const updated = recentlyUsedUsers.filter(id => id !== userId);
        
        set({ recentlyUsedUsers: updated });
        
        log.user.action('recent_user_removed', 'SharingStore', { userId });
      },

      setPreferredVisibility: (visibility: string) => {
        set({ preferredVisibility: visibility });
        
        log.user.action('preferred_visibility_set', 'SharingStore', { visibility });
      },

      clearRecentGroups: () => {
        set({ recentlyUsedGroups: [] });
        log.user.action('recent_groups_cleared', 'SharingStore');
      },

      clearRecentUsers: () => {
        set({ recentlyUsedUsers: [] });
        log.user.action('recent_users_cleared', 'SharingStore');
      },

      reset: () => {
        set(initialState);
        log.user.action('sharing_store_reset', 'SharingStore');
      }
    }),
    {
      name: 'smartdoc-sharing-preferences',
      // Only persist the state, not the actions
      partialize: (state) => ({
        recentlyUsedGroups: state.recentlyUsedGroups,
        recentlyUsedUsers: state.recentlyUsedUsers,
        preferredVisibility: state.preferredVisibility,
        maxRecentItems: state.maxRecentItems
      })
    }
  )
);

// Convenience hooks for specific use cases
export const useRecentGroups = () => {
  const recentlyUsedGroups = useSharingStore(state => state.recentlyUsedGroups);
  const addRecentGroup = useSharingStore(state => state.addRecentGroup);
  const removeRecentGroup = useSharingStore(state => state.removeRecentGroup);
  
  return { recentlyUsedGroups, addRecentGroup, removeRecentGroup };
};

export const useRecentUsers = () => {
  const recentlyUsedUsers = useSharingStore(state => state.recentlyUsedUsers);
  const addRecentUser = useSharingStore(state => state.addRecentUser);
  const removeRecentUser = useSharingStore(state => state.removeRecentUser);
  
  return { recentlyUsedUsers, addRecentUser, removeRecentUser };
};

export const usePreferredVisibility = () => {
  const preferredVisibility = useSharingStore(state => state.preferredVisibility);
  const setPreferredVisibility = useSharingStore(state => state.setPreferredVisibility);
  
  return { preferredVisibility, setPreferredVisibility };
};